import { useContext, useState, useEffect, useCallback } from 'react';
import { User } from '../auth/types';
import Logger from '../utils/logger';
import { AuthContext, supabase } from '../components/auth/AuthProvider';

export function useAuth() {
  const { session, isLoading, user: supabaseUser } = useContext(AuthContext);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [hasCheckedPermissions, setHasCheckedPermissions] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const isAuthenticated = !!session;

  const clearPermissions = useCallback(() => {
    setPermissions([]);
    setHasCheckedPermissions(false);
  }, []);

  useEffect(() => {
    if (!session) {
      clearPermissions();
    }
  }, [session, clearPermissions]);

  useEffect(() => {
    const checkPermissions = async () => {
      if (isAuthenticated && !hasCheckedPermissions) {
        try {
          const jwt = session?.access_token || '';
          let userPermissions: string[] = [];
          if (jwt) {
            try {
              const payload = JSON.parse(atob(jwt.split('.')[1]));
              const userRole = payload.user_role === 'user';
              if (userRole) {
                userPermissions = ['jua:api'];
              }
            } catch (jwtError) {
              Logger.error('Failed to parse JWT token', jwtError);
            }
          }
          setPermissions(userPermissions);
        } catch (error) {
          Logger.error('Failed to get permissions', error);
          setPermissions([]);
          setError(error instanceof Error ? error : new Error('Unknown error'));
        }
        setHasCheckedPermissions(true);
      }
    };

    checkPermissions();
  }, [isAuthenticated, hasCheckedPermissions, session]);

  const logout = useCallback(async () => {
    try {
      Logger.info('User logging out, clearing auth state');
      clearPermissions();

      try {
        // Prefer local logout to avoid global revoke issues (403 errors)
        Logger.info('Performing local logout to avoid server errors');
        const { error } = await supabase.auth.signOut({ scope: 'local' });

        if (error) {
          Logger.warn('Supabase logout returned error, but continuing with local cleanup', error);
        }

        Logger.info('Logout completed successfully');
      } catch (signOutError) {
        // If Supabase logout fails completely, we still want to clear local state
        Logger.warn('Supabase signOut failed, performing manual cleanup', signOutError);

        // Fallback: clear common auth storage keys manually
        const authKeys = ['sb-auth-token', 'supabase.auth.token'];

        authKeys.forEach((key) => {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        });
      }
    } catch (error) {
      Logger.error('Logout function failed', error);
    }
  }, [clearPermissions]);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      return permissions.includes(permission);
    },
    [permissions],
  );

  const needsProfileCompletion = useCallback((): boolean => {
    if (!supabaseUser) return false;

    const displayName =
      supabaseUser.user_metadata?.full_name ||
      supabaseUser.user_metadata?.name ||
      supabaseUser.user_metadata?.display_name;
    const country = supabaseUser.user_metadata?.country;
    const howFoundUs = supabaseUser.user_metadata?.how_found_us;
    const userType = supabaseUser.user_metadata?.user_type;
    const companyName = supabaseUser.user_metadata?.company_name;
    const roleDescription = supabaseUser.user_metadata?.role_description;
    const companySegment = supabaseUser.user_metadata?.company_segment;

    const hasDisplayName = Boolean(displayName && displayName.trim());
    const hasCountry = Boolean(country && country.trim());
    const hasHowFoundUs = Boolean(howFoundUs && howFoundUs.trim());
    const hasUserType = Boolean(userType && userType.trim());
    const hasRoleDescription = Boolean(roleDescription && roleDescription.trim());
    const hasCompanySegment = userType !== 'business' || Boolean(companySegment);

    const hasCompanyName = userType !== 'business' || Boolean(companyName && companyName.trim());

    return (
      !hasDisplayName ||
      !hasCountry ||
      !hasHowFoundUs ||
      !hasUserType ||
      !hasCompanyName ||
      !hasCompanySegment ||
      !hasRoleDescription
    );
  }, [supabaseUser]);

  const user: User | null = supabaseUser
    ? {
        sub: supabaseUser.id,
        email: supabaseUser.email || '',
        name:
          supabaseUser.user_metadata?.full_name ||
          supabaseUser.user_metadata?.name ||
          supabaseUser.user_metadata?.display_name ||
          supabaseUser.identities?.[0]?.identity_data?.full_name ||
          supabaseUser.identities?.[0]?.identity_data?.name ||
          supabaseUser.email ||
          'User',
        permissions,
      }
    : null;

  const refreshUser = useCallback(async () => {
    try {
      const {
        data: { user: refreshedUser },
      } = await supabase.auth.getUser();
      return refreshedUser;
    } catch (error) {
      Logger.error('Failed to refresh user data', error);
      return null;
    }
  }, []);

  return {
    isAuthenticated,
    isLoading,
    user,
    error,
    logout,
    hasPermission,
    isPermissionsLoaded: hasCheckedPermissions,
    needsProfileCompletion,
    refreshUser,
  };
}
